import React, { useState, useEffect } from 'react'
import { apiClient } from '../services/apiClient'
import { useHotelAdminStore } from '../store'
import Modal from './Modal'
import Button from './Button'
import QRCode from 'qrcode.react'

interface InvitationCode {
  code: string
  is_active: boolean
  created_at: string
  expires_at: string
  used_at?: string
  used_by_admin_id?: number
}

export default function AdminInvitationCodeSection() {
  const { admin } = useHotelAdminStore()
  const [codes, setCodes] = useState<InvitationCode[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [newCodeUrl, setNewCodeUrl] = useState('')
  const [showNewCode, setShowNewCode] = useState(false)
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, codeToDeactivate: '' })
  const [guestRegistrationUrl, setGuestRegistrationUrl] = useState<string>('')

  // Load codes on mount
  useEffect(() => {
    loadCodes()
  }, [])

  // Set guest registration URL when admin data is available
  useEffect(() => {
    if (admin?.admin_id) {
      const baseUrl = window.location.origin
      setGuestRegistrationUrl(`${baseUrl}/guest/${admin.admin_id}`)
    }
  }, [admin])

  const loadCodes = async () => {
    try {
      setIsLoading(true)
      setError('')
      const response = await apiClient.listMyInvitationCodes()
      if (response.status === 'ok') {
        setCodes(response.codes || [])
      }
    } catch (err: any) {
      setError('Ошибка при загрузке кодов: ' + (err.message || ''))
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateCode = async () => {
    try {
      setIsLoading(true)
      setError('')
      setSuccess('')
      
      const response = await apiClient.createInvitationCode()
      
      if (response.status === 'ok') {
        setNewCodeUrl(response.url)
        setShowNewCode(true)
        setSuccess('Код приглашения успешно создан!')
        
        // Reload codes after a delay
        setTimeout(() => {
          loadCodes()
          setShowNewCode(false)
        }, 3000)
      }
    } catch (err: any) {
      setError('Ошибка при создании кода: ' + (err.message || ''))
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeactivateCode = async (code: string) => {
    setConfirmModal({ isOpen: true, codeToDeactivate: code })
  }

  const handleConfirmDeactivate = async () => {
    const code = confirmModal.codeToDeactivate
    setConfirmModal({ isOpen: false, codeToDeactivate: '' })
    
    try {
      setError('')
      await apiClient.deactivateInvitationCode(code)
      setSuccess('Код успешно деактивирован')
      loadCodes()
    } catch (err: any) {
      setError('Ошибка при деактивации кода: ' + (err.message || ''))
    }
  }

  const handleCopyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      alert('Ссылка скопирована в буфер обмена!')
    })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-2xl font-bold text-gray-800 mb-6">🔐 Управление доступом</h3>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          ⚠️ {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          ✅ {success}
        </div>
      )}

      {/* Guest Registration QR Code Section */}
      <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <h4 className="text-lg font-semibold text-gray-800 mb-3">📱 Регистрация гостей</h4>
        <p className="text-gray-600 mb-4">
          Гости могут использовать этот QR-код для быстрой регистрации в вашем отеле.
        </p>
        
        {guestRegistrationUrl && (
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="bg-white p-4 rounded-lg shadow-md">
              <QRCode 
                value={guestRegistrationUrl}
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>
            
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-2">Ссылка для регистрации:</p>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={guestRegistrationUrl}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono border"
                />
                <button
                  onClick={() => handleCopyToClipboard(guestRegistrationUrl)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold"
                >
                  Копировать
                </button>
              </div>
              <a 
                href={guestRegistrationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                🔗 Открыть страницу регистрации
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Create New Code Section */}
      <div className="mb-8 p-6 bg-indigo-50 rounded-lg border border-indigo-200">
        <h4 className="text-lg font-semibold text-gray-800 mb-3">Создать новый код</h4>
        <p className="text-gray-600 mb-4">
          Генерируйте уникальные коды приглашения для партнеров и новых администраторов отелей.
        </p>
        
        {showNewCode && newCodeUrl && (
          <div className="mb-4 p-4 bg-white border-2 border-green-500 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Ссылка для регистрации:</p>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newCodeUrl}
                readOnly
                className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono border"
              />
              <button
                onClick={() => handleCopyToClipboard(newCodeUrl)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-semibold"
              >
                Копировать
              </button>
            </div>
            <p className="text-xs text-gray-500">Отправьте эту ссылку партнеру для регистрации</p>
          </div>
        )}

        <button
          onClick={handleCreateCode}
          disabled={isLoading}
          className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold disabled:opacity-50"
        >
          {isLoading ? 'Создание...' : '+ Создать новый код'}
        </button>

        <p className="text-xs text-gray-500 mt-3">
          Максимум 10 кодов в день. Код действует 30 дней.
        </p>
      </div>

      {/* Existing Codes List */}
      <div>
        <h4 className="text-lg font-semibold text-gray-800 mb-4">Коды приглашения для партнеров</h4>
        
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Загрузка...</div>
        ) : codes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>Нет созданных кодов</p>
            <p className="text-sm">Создайте первый код приглашения выше</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-2 px-3 text-sm font-semibold text-gray-600">Код</th>
                  <th className="text-left py-2 px-3 text-sm font-semibold text-gray-600">Статус</th>
                  <th className="text-left py-2 px-3 text-sm font-semibold text-gray-600">Создан</th>
                  <th className="text-left py-2 px-3 text-sm font-semibold text-gray-600">Истекает</th>
                  <th className="text-left py-2 px-3 text-sm font-semibold text-gray-600">Использован</th>
                  <th className="text-left py-2 px-3 text-sm font-semibold text-gray-600">Действие</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((code) => (
                  <tr key={code.code} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-3 text-sm font-mono text-gray-800">{code.code}</td>
                    <td className="py-3 px-3 text-sm">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          code.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {code.is_active ? '✓ Активен' : '✗ Деактивирован'}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600">
                      {formatDate(code.created_at)}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600">
                      {formatDate(code.expires_at)}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600">
                      {code.used_at ? (
                        <span className="text-green-600 font-semibold">
                          {formatDate(code.used_at)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-sm">
                      {code.is_active && !code.used_at && (
                        <button
                          onClick={() => handleDeactivateCode(code.code)}
                          className="text-red-600 hover:text-red-800 font-semibold text-xs"
                        >
                          Деактивировать
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-blue-900 mb-2">📌 Как это работает:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>✓ Генерируйте код приглашения</li>
          <li>✓ Поделитесь ссылкой с администратором нового отеля</li>
          <li>✓ Партнер переходит по ссылке и регистрируется</li>
          <li>✓ Код можно использовать только один раз</li>
          <li>✓ Код действует 30 дней с момента создания</li>
        </ul>
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmModal.isOpen}
        title="Деактивировать код приглашения"
        onClose={() => setConfirmModal({ isOpen: false, codeToDeactivate: '' })}
        actions={
          <div className="flex gap-3">
            <button
              onClick={() => setConfirmModal({ isOpen: false, codeToDeactivate: '' })}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-semibold"
            >
              Отмена
            </button>
            <button
              onClick={handleConfirmDeactivate}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-semibold"
            >
              Деактивировать
            </button>
          </div>
        }
      >
        <p className="text-gray-700">Вы уверены? Этот код больше нельзя будет использовать.</p>
      </Modal>
    </div>
  )
}
